import { useCallback, useEffect, useRef, useState } from "react";
import { tableViewsApi } from "@/lib/api";

/**
 * Lädt & speichert Tabellen-Präferenzen (Spalten, Sortierung, Seitengröße,
 * Filter) und benannte Ansichten pro Nutzer serverseitig. LocalStorage dient
 * als Sofort-Fallback, damit die UI ohne Wartezeit den letzten Zustand zeigt.
 */
export function useTablePrefs(tableKey, defaultPrefs) {
  const lsKey = `tableprefs:${tableKey}`;
  const [prefs, setPrefsState] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(lsKey) || "null");
      if (cached) return { ...defaultPrefs, ...cached, filters: { ...(defaultPrefs.filters || {}), ...(cached.filters || {}), search: "" } };
    } catch (e) { console.debug("Tabellen-Prefs-Cache:", e); }
    return defaultPrefs;
  });
  const [views, setViews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    let active = true;
    tableViewsApi.get(tableKey).then(({ data }) => {
      if (!active) return;
      if (data.prefs && Object.keys(data.prefs).length > 0) {
        setPrefsState((prev) => ({ ...prev, ...data.prefs, filters: { ...(prev.filters || {}), ...(data.prefs.filters || {}), search: "" } }));
      }
      setViews(Array.isArray(data.views) ? data.views : []);
      setLoaded(true);
    }).catch((e) => { console.debug("Tabellen-Ansichten laden:", e); setLoaded(true); });
    return () => { active = false; };
  }, [tableKey]);

  const persist = useCallback((next) => {
    const toSave = { ...next, filters: { ...(next.filters || {}), search: "" } };
    try { localStorage.setItem(lsKey, JSON.stringify(toSave)); } catch (e) { console.debug("Tabellen-Prefs speichern:", e); }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      tableViewsApi.savePrefs(tableKey, toSave).catch((e) => console.debug("Tabellen-Prefs Sync:", e));
    }, 700);
  }, [tableKey, lsKey]);

  const setPrefs = useCallback((updater) => {
    setPrefsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      persist(next);
      return next;
    });
  }, [persist]);

  const saveView = useCallback(async (name, config) => {
    const { data } = await tableViewsApi.addView(tableKey, name, config);
    setViews((prev) => [...prev, data]);
    return data;
  }, [tableKey]);

  const deleteView = useCallback(async (viewId) => {
    await tableViewsApi.deleteView(tableKey, viewId);
    setViews((prev) => prev.filter((v) => v.id !== viewId));
  }, [tableKey]);

  return { prefs, setPrefs, views, saveView, deleteView, loaded };
}
